/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/dashboard-common/dist/core/Model',
	'./FilterValuesCollection',
	'./FilterSpecHelper',
	'underscore'
], function(Model, FilterValuesCollection, FilterSpecHelper, _) {
	'use strict';

	/**
	 * Model which represents a filter applied on data
	 */
	var FilterEntry = null;


	FilterEntry = Model.extend({
		whitelistAttrs: ['id', 'idSuffix', 'action', 'columnId', 'values', 'valueDataItem', 'excludedValues', 'conditions', 'operator', 'type', 'aggregationType','visualizationId', 'preOrPost', 'overrideHierarchyDefaultSelection', 'filterBins', 'binsLabel'],

		FILTERACTION_KEEPEXCLUDE: 'keepexclude',  //the keep/exclude filter action (special because keepexclude allows multiple entries per item.)

		/**
		 * Used for initializing and finding the appropriate id for a filter entry
		 * from the other field values that determine the id.
		 * @param columnIdOrDataPointId	a columnId or, for dataPoints, the baseId for the dataPoint (usu a stringified array of the items that the point is based on.)
		 * @param aggregationType the aggregation type (ordinals only)
		 * @param idSuffix - appended to the base id to allow keyed access on multiple keep/exclude filters on the same item.
		 */
		determineIdValue: function(columnIdOrDataPointId, aggregationType, idSuffix) {
			return FilterSpecHelper.getFilterId(columnIdOrDataPointId, aggregationType, idSuffix);
		},

		nestedCollections: {
			values: FilterValuesCollection,
			excludedValues: FilterValuesCollection
		},

		init: function(attrs) {
			//The id is based on the columnId or dataPointId (an array of columnIds) + any supplied aggregation type + any supplied suffix.
			this.id = attrs.filterBins ? attrs.id : attrs.id || this.determineIdValue((attrs.columnId || attrs.dataPointId), attrs.aggregationType, attrs.idSuffix);
			this.isModelFilter = (!attrs.operator && !attrs.value && !attrs.values);
			this.visualizationId = attrs.visualizationId;
			if (attrs.filterBins) {
				this.filterBins = attrs.filterBins;
				this.binsLabel = attrs.binsLabel;
			}
			FilterEntry.inherited('init', this, arguments);
		},

		/**
		 * A compound filter is a filterEntry where a single object does not completely define the filter on its own but is part of a
		 * tree structure with other filterEntry instances.  A compound filter is needed to do dataPoint filtering because
		 * 1) multiple 'in' values (each defined as a filterEntry) must be 'anded' together to define a point and
		 * 2) multiple points must be 'ored' together to produce a set of points.
		 * 3) the set of points must be 'not' ed to produce a negative data point filter.
		 * @returns true if this filterEntry is the root of a compound() filter tree (or sub-tree)
		 */
		isCompound: function() {
			return FilterSpecHelper.isCompound(this);
		},

		/**
		 * @returns true if this filter entry is an ordinal filter (ordinal filters have an aggregation type).
		 */
		isOrdinalFilter: function() {
			return FilterSpecHelper.isOrdinal(this);
		},

		/**
		 * @returns true if this filter entry is a model filter
		 */
		getIsModelFilter: function() {
			return this.isModelFilter;
		},


		/**
		 * @returns true if this is an 'additional filter' (ie: a second/third filter on a given item or tuple)
		 */
		isAdditionalFilter: function() {
			return FilterSpecHelper.isAdditional(this);
		},

		/**
		 * @returns an array of all of the leaf level filter entry objects (ones with columnId's).
		 */
		collectLeafFilterEntries: function(leafFilterEntries) {
			if (this.isCompound()) {
				_.each(this.values.models, function(model) {
					if (model.value) {
						model.value.collectLeafFilterEntries(leafFilterEntries);
					}
				}.bind(this));
			} else {
				leafFilterEntries.push(this);
			}
		},

		/**
		 * Match 'terms' of this compound filter (eg: 'and' <term, term> or 'or' <term, term> against a set of
		 * columnIdValuePairs (a map of columnId to value).
		 * @param columnIdValuePairs - a set of columnIds and values that must be found in the compound filter to match
		 *								match rules vary by operator.
		 *									If the operator is 'or', only one columnId/value must match
		 *									otherwise ALL columnId/values must match
		 * NOTE: if values in the columnIdValuePair map are empty [], only the id's must match
		 * @param operatorVector - (optional) if specified, the path of operators must match as well
		 * 					eg: ['or','and','in'] would only match filters whose structure operators at each depth match.
		 * @returns true if the columnId/value pairs in at least 1 term of this compound filter pass the matching rules.
		 */
		compoundFilterMatches: function(columnIdValuePairs, operatorVector) {
			//If an operatorVector is specified, reject any filter whose chain of operators don't match.
			if (operatorVector && operatorVector.length > 0 && this.operator !== operatorVector[0]) {
				return false;
			}

			if (this.isCompound()) {
				return this._compoundValuesMatches(columnIdValuePairs, operatorVector);
			}

			return this._checkColumnIdAndValues(columnIdValuePairs);
		},

		/**
		 * See compoundFilterMatches for description.
		 */
		_compoundValuesMatches: function(columnIdValuePairs, operatorVector) {
			let matchCount = 0;
			let filterBins = false;
			this.values.each(function(value) {
				if (value.value.compoundFilterMatches(columnIdValuePairs, (operatorVector) ? _.rest(operatorVector) : null)) {
					matchCount++;
				}
				if (value.filterBins || value.value.filterBins) {
					filterBins = true;
				}
			});
			if (filterBins) {
				return matchCount > 0;
			}
			return ((this.operator === 'or' && matchCount > 0) || matchCount === this.values.size() ? true : false);
		},

		_checkColumnIdAndValues: function(columnIdValuePairs) {
			//LEAF LEVEL:, Ensure that this columnId is in the columnId set  (or that there were no values to match).
			if (columnIdValuePairs[this.columnId] === undefined) {
				return false; //No columnId...this particular term didn't match
			} else if (columnIdValuePairs[this.columnId].length === 0) {
				return true; //An entry for this column and no values to match means this term matched
			}

			//Check that every value associated with this column exists in the valuelist.
			var valuesToMatch = columnIdValuePairs[this.columnId];
			for (var i = 0; i < valuesToMatch.length; ++i) {
				var value = valuesToMatch[i];
				if (!this.values.get(value.u)) {
					return false;
				}
			}
			return true;
		},

		/**
		 * @returns an array for use in reporting data point filter details (getFilterInfo).
		 * eg: [ {3:'2005', 5:'Camping'}, {3:'2006', 5:'Golf'}] - this allows easy ability to report or count points in a datapoint array.
		 */
		getSelectedDataPointArray: function() {
			var dpa = [];
			this._loadSelectedDataPointArray(dpa);
			return dpa;
		},

		/**
		 * recursively build up a selectedColumnId map as a map of form.
		 * This is used to quickly build a unique map of all of the columnId's in a compound filter.
		 * {
		 *		idN: 'idN'
		 * }
		 */
		loadSelectedColumnIdMap: function(selectedColumnIdMap) {
			if (this.columnId !== undefined) {
				selectedColumnIdMap[this.columnId] = this.columnId;
			}
			this.values.each(function(value) {
				if (value.value && value.value.loadSelectedColumnIdMap) {
					value.value.loadSelectedColumnIdMap(selectedColumnIdMap);
				}
			}.bind(this));
		},

		/**
		 * @returns the columnId's which are part of this filter as an array of 1 or more entries
		 * For a simple filter, this would be the same as the columnId member of the single filterEntry object.
		 * For a compound/dataPoint filter, this would be a unique array of columnIds collected from the terms of the compound filter.
		 */
		getColumnIds: function() {
			var cidMap = {};
			this.loadSelectedColumnIdMap(cidMap);
			return _.keys(cidMap);
		},

		/**
		 * Remove a specific term from this data point filter (ie: one of the 'and' terms from its containing 'or'.
		 */
		removeDataPointFilterTerm: function(dataPointMap) {
			//This is a matching data point.
			if (this.isCompound()) {
				if (this.operator === 'and' && this.compoundFilterMatches(dataPointMap)) {
					return this;
				}

				var match = this.values.find(function(value) {
					return (value.value.removeDataPointFilterTerm(dataPointMap) !== null);
				});

				if (this.operator === 'or' && match) {
					this.values.remove(match);
				}
			}
			return null;
		},

		/**
		 * Return the filter query spec for this filter entry.
		 * The query expression is of form:
		 * (for example)
		 * { type: "pre|post", expression: {itemId: 'itemId1', operator: 'in', values: [ v1, v2 ]} }
		 * or
		 * { type: "pre|post", expression: {itemId: 'itemId2', operator: 'between', values: [ min, max ] }
		 *
		 * For inclusion/exclusion of null categories:
		 * filterValues internally store an array of values that can include null to be included/excluded along with the other values.
		 * When generating a filterSpec, this value must be extracted and set as
		 * {
		 *	type: "pre|post",
		 *	expression: {
		 *		'or': [	{ itemId: 'ItemId1', operator: 'isnull' },
		 *				{ itemId: 'ItemId1', operator: 'in', values: [ v1, v2 ] }
		 *  	]
		 *  }
		 * }
		 * To use the above query to include null,
		 * set values = ["2017-02-08", "2017-02-09", "null"]
		 *
		 * For compound filters (like dataPoint filters), a similar technique is used.
		 * {
		 *	type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 *  for compundFilter that contains filter on binned dataItem , the filter on binned dataItem will be removed
		 *  for example:
		 *  {
		 *  type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ dataItem: 'itemId-binned', operator: 'in', values: [v2]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 * will be changed to
		 * {
		 *  type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 *
		 * @param options - options for query spec generation.
		 * @returns a filter query spec for this filter entry.
		 */
		toQuerySpec: function(options) {
			options = options || {};
			if (this.isCompound()) {
				var expressionSpec = {};
				// datapoint Exclude has 'not' as first operator
				if (typeof options.isDataPointExclude === 'undefined') {
					options.isDataPointExclude = this.operator === 'not' ? true : false;
				}
				//At the root, we need to generate the filter type (pre|post) and the expression.
				var querySpec = (options.isSubExpression) ?
					expressionSpec : {
						type: this.preOrPost || 'pre',
						expression: expressionSpec
					};
				expressionSpec[this.operator] = [];

				//If the values in this tuple contains one or more numbers, we need to create a display type datapoint filter.
				var tupleContainsNumber = this._filterValuesContainNumbers();

				this.values.each(function(value) {
					if (value.value && value.value.toQuerySpec) {
						if (!value.value.filterBins) {
							var subExpressionOptions = _.extend(options, {
								isSubExpression: true,
								tupleContainsNumber: tupleContainsNumber
							});
							let subExpressionSpec = value.value.toQuerySpec(subExpressionOptions);
							if (!_.isEmpty(subExpressionSpec)) {
								expressionSpec[this.operator] = expressionSpec[this.operator].concat(subExpressionSpec);
								if (this.operator === 'and' && options.isDataPointExclude && !options.nullIncludedInValueList &&
									(!options.shouldApplyIsNullValueExpression || options.shouldApplyIsNullValueExpression(value.value))) { // Should not add isnull for OLAP sources
									expressionSpec[this.operator] = expressionSpec[this.operator].concat(value.value._buildNullValueExpresstionQuerySpec(options));
								}
							}
						}
					}
				}.bind(this));


				if (this.operator === 'not') {
					if (!options.isSubExpression) {
						//Special case for negative dataPoint filters.  A 'not' operator is generated at the root.
						//The term of the not operator is not an array like it is for other operators.
						//The syntax is: (see dss spec for details.)
						//not: { or: [] } and not
						//not: [{ or: []}]
						querySpec.expression.not = querySpec.expression.not[0];
					} else {
						// NOT operator supports only one value
						expressionSpec[this.operator] = expressionSpec[this.operator][0];
					}

					if (querySpec.expression && typeof querySpec.expression.not === 'undefined') {
						querySpec.expression = {};
					}
				} else if (expressionSpec[this.operator] && !expressionSpec[this.operator].length) {
					delete expressionSpec[this.operator];
				}
				return querySpec;
			} else {
				// Edge Exclude has 'notin' as first operator
				if (typeof options.isEdgeExclude === 'undefined') {
					options.isEdgeExclude = this.operator === 'notin' ? true : false;
				}
			}
			return this._leafToQuerySpec(options);
		},

		//create expressions { itemId: 'itemId1',operator: isnull } or { itemId: 'itemId1',operator: isnotnull }
		// based on it is edge exclude or data point exclude
		_buildNullValueExpresstionQuerySpec: function(options) {
			let operator;
			if (options.isDataPointExclude) {
				operator = 'isnotnull';
			} else if (options.isEdgeExclude) {
				operator = 'isnull';
			}
			if (operator) {
				let expressionQuerySpec = {
					itemId: this.columnId,
					operator: operator
				};
				this._setBinningItemId(expressionQuerySpec);
				return expressionQuerySpec;
			}
		},

		/**
		 * Compound values need to be post-processed when loaded fromJSON to allow a recursive model in a collection
		 * (where a model has a reference to another instance of itself).
		 */
		_compoundFilterFromJSON: function() {
			_.each(this.values.models, function(model) {
				if (model.value && model.value.operator) {
					model.value = new FilterEntry(model.value, model);
					model.value._compoundFilterFromJSON();
				}
			});
		},

		/**
		 * recursively builds an array for use in reporting filter information
		 * [ {3:'2005', 5:'Camping'}, {3:'2006', 5:'Golf'}]
		 * @param dataPointArray - the array to be loaded.
		 */
		_loadSelectedDataPointArray: function(dataPointArray) {
			if (this.isCompound()) {
				var dataPoint = {};
				this.values.each(function(value) {
					var dataPointPart = value.value._loadSelectedDataPointArray && value.value._loadSelectedDataPointArray(dataPointArray);
					if (dataPointPart) {
						dataPoint[dataPointPart[0]] = dataPointPart[1];
					}
				});
				if (_.keys(dataPoint).length > 0) {
					dataPointArray.push(dataPoint);
				}
			} else if (this.columnId) {
				return [this.columnId, this.values.models[0].value];
			}
		},

		//This function returns true if the values of a filterEntry (simple or compound contain a number.)
		//For compound filters, this function recursively searches all of the branches.
		//@returns true if the valueset does.
		_filterValuesContainNumbers: function() {
			if (this.isCompound()) {
				return this.values.find(function(valueObject) {
					//Each value in the values collection of a compound entry is another filterEntry.
					return valueObject.value._filterValuesContainNumbers && valueObject.value._filterValuesContainNumbers();
				});
			}
			//At the leaf, the values collection contains an array of FilterValue objects (each having an id and value)
			return this.values && this.values.find(function(valueObject) { return _.isNumber(valueObject.value.d); });
		},

		isRangeOperator: function() {
			return FilterSpecHelper.isRange(this);
		},

		_setBinningItemId(querySpec) {
			if (this.filterBins) {
				delete querySpec.itemId;
				querySpec.dataItem = this.id;
			}
		},

		_leafToQuerySpec: function(options) {
			if (this.isModelFilter) {
				return {
					'type': 'pre',
					'expression': {
						'filterId': this.columnId
					}
				};
			}else if( this.conditions ){
				return this._buildConditionsQuerySpec(options);
			} else {

				var expressionQuerySpec = {
					operator: this.operator
				};
				//If this is a range filter, it might be a dataItem filter not a pure column filter.
				//This handles cases where the aggregation type of a range corresponds to the item.
				if (options.associatedDataItemAPI) {
					expressionQuerySpec.dataItem=options.associatedDataItemAPI.getUniqueId();
				} else if (this.filterBins) {
					expressionQuerySpec.dataItem = this.id;
				} else {
					expressionQuerySpec.itemId=this.columnId;
				}
				if (options.tupleContainsNumber) {
					expressionQuerySpec.valueType = 'display';
				} else if (this.type) {
					expressionQuerySpec.valueType = this.type;
				}

				if (this.values.models && this.values.models.length > 0) {
					var aValueObject = this.values.toArray();

					//Update the expressionQuerySpec with any nullValue inclusion/exclusion (if any)
					expressionQuerySpec = this._buildNullValuesQuerySpec(expressionQuerySpec, aValueObject, options);
				} else {
					// When there is no filter values
					if (this.valueDataItem) {
						expressionQuerySpec.valueDataItem = this.valueDataItem;
					} else if (expressionQuerySpec.operator === 'notin') {
						/**
						 * Convert exclude filter to
						 * {
						 * 	"operator": "in",
						 * 	"itemId": "Sheet1.address",
						 * 	"valueDataItem": "id1608643800"
						 * }
						 */
						const newQueryDataItem = options && options.addExtraQueryDataItem && options.addExtraQueryDataItem(this.columnId);
						if (newQueryDataItem) {
							expressionQuerySpec.operator = 'in';
							expressionQuerySpec.valueDataItem = newQueryDataItem.id;
						}
					}
				}
				let combiningQuerySpec = this._buildEdgeNullValueExpresstionQuerySpec(expressionQuerySpec, options);
				if (combiningQuerySpec) {
					expressionQuerySpec = combiningQuerySpec;
				}
				//By default preOrPost should be post for range filters and pre for category filters.
				var defaultPreOrPost=(this.isRangeOperator()) ? 'post' : 'pre';
				//If there is any kind of user-specified preOrPost setting, use it.
				var preOrPost=this.preOrPost || options.type || defaultPreOrPost;

				var filterQuerySpec = (options.isSubExpression) ?
					expressionQuerySpec : {
						type: preOrPost,
						expression: expressionQuerySpec
					};

				return filterQuerySpec;
			}
		},

		/**
		 * expression: { itemId: 'abcItem', operator: 'notin', values: ['a', 'b', 'c'] } will be:
		 * or: [{ itemId: 'abcItem',operator: isnull }, { itemId: 'abcItem', operator: 'notin', values: ['a', 'b', 'c'] } ]
		 * If filter is against relational data source.
		 * Returns null if is OLAP data source and the exclude expression will stay unchanged
		 */
		_buildEdgeNullValueExpresstionQuerySpec: function(expressionQuerySpec, options) {
			if (!this.filterBins &&
				!expressionQuerySpec.valueDataItem && // exclude filter with empty values is converted to 'in valueDataItem...' so skip
				this.operator === 'notin' &&
				options.isEdgeExclude &&
				!options.nullIncludedInValueList &&
				(!options || !options.shouldApplyIsNullValueExpression || options.shouldApplyIsNullValueExpression(this))) {
				let combiningQuerySpec = {};
				combiningQuerySpec['or'] = [this._buildNullValueExpresstionQuerySpec(options), expressionQuerySpec];
				return combiningQuerySpec;
			}
			return null;
		},

		_buildNullValuesQuerySpec: function(expressionQuerySpec, aValueObject, options) {
			options = options || {};
			options.nullIncludedInValueList = false;
			//The spec expects 'value' for 'eq'/'noteq' and 'values' for 'in'/'notin'
			//var valueOrValues = (this.operator === 'eq') ? 'value' : 'values';
			var valueOrValues = 'values';
			//Set the polarity of how values will be filtered (include/exclude).
			var includeType = (this.operator.indexOf('not') === 0) ? false : true;

			// Get rid of the explicit null entries in the array
			var valuesExcludingNull = _.without(aValueObject, null, 'null');
			// Get rid of the entries that have null u and d values
			valuesExcludingNull = _.filter(valuesExcludingNull, function(entry) {
				return (entry.u !== null && entry.d !== null) && (entry.u !== 'null' && entry.d !== 'null');
			});
			valuesExcludingNull = _.pluck(valuesExcludingNull, (this.type === 'display' || options.tupleContainsNumber) ? 'd' : 'u');
			expressionQuerySpec[valueOrValues] = valuesExcludingNull;

			if (valuesExcludingNull.length < aValueObject.length) {
				options.nullIncludedInValueList = true;
				var filterNullsQuerySpec = {
					itemId: this.columnId,
					operator: (includeType) ? 'isnull' : 'isnotnull'
				};

				this._setBinningItemId(filterNullsQuerySpec);
				if (this.type) {
					filterNullsQuerySpec.valueType = this.type;
				}

				if (valuesExcludingNull.length > 0) {
					var combiningOperator = (includeType) ? 'or' : 'and';
					var combiningQuerySpec = {};
					combiningQuerySpec[combiningOperator] = [filterNullsQuerySpec, expressionQuerySpec];
					expressionQuerySpec = combiningQuerySpec;
				} else {
					expressionQuerySpec = filterNullsQuerySpec;
				}
			}
			return expressionQuerySpec;
		},

		_buildConditionsQuerySpec: function(options){
			var valuesExp;
			if( this.values && !this.values.isEmpty()) {
				var aValueObject = this.values.toArray();
				var aValues = _.pluck(aValueObject, this.type === 'display' ? 'd' : 'u');
				valuesExp = {
					valueType: this.type,
					'operator' : 'in',
					'itemId' : this.columnId,
					'values' : aValues
				};
				this._setBinningItemId(valuesExp);
			}
			var excludesExp;
			if( this.excludedValues && !this.excludedValues.isEmpty() ) {
				var aExcludedValueObject = this.excludedValues.toArray();
				var aExcludedValues = _.pluck(aExcludedValueObject, this.type === 'display' ? 'd' : 'u');
				excludesExp = {
					valueType: this.type,
					'operator' : 'notin',
					'itemId' : this.columnId,
					'values' : aExcludedValues
				};
				this._setBinningItemId(excludesExp);
			}

			var part1 = valuesExp ? { 'or': [this.conditions, valuesExp] } : this.conditions;
			var filterSpec = excludesExp ?  { 'and': [part1, excludesExp] } : part1;

			if( this.operator === 'notin'){
				filterSpec = { 'not': filterSpec };
			}
			if( options.isSubExpression ){
				return filterSpec;
			}else{
				return {
					type: options.type || 'pre',
					expression: filterSpec
				};
			}
		}
	});

	return FilterEntry;
});
